/**
 * Component: Hero Carousel
 * ------------------------------------------------------------------------------
 * A set of scripts to create carousel from static section.
 *
 */
import Carousel from "~/api/carousel"

import classes from "~/config/classes"

/**
 * DOM selectors.
 */
const selectors = {
  carousel: '[js-carousel="hero-carousel"]',
  video: '[js-hero-carousel="video"]',
  countdown: '[js-hero-carousel="countdown"]',
  countdownDuringCta: '[js-hero-carousel="cta"]',
  countdownOverCta: '[js-hero-carousel="countdown-cta"]',
}

/**
 * Create a new hero carousel object.
 */
export default () => {

  /**
   * Initialise component.
   */
  function init() {
    constructCarousel()
    setVideoAutoplays()
    constructCountdownTimers()
  }

  /**
   * Turns autoplay on and off depending on reduced motion settings.
   */
  function setVideoAutoplays() {
    const videoSlides = [...document.querySelectorAll(selectors.video)]

    if (!prefersReducedMotion) {
      return
    }

    videoSlides.forEach((video) => {
      video.setAttribute("autoplay", false)
    })
  }

  /**
   * Returns the reduced motion settings.
   * @returns {Boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  /**
   * Toggles whether the carousel arrows are displayed or not.
   * @param {Object} carousel - HTML element comprising the carousel.
   */
  function showArrows(carousel) {
    const arrows = carousel.querySelector(".splide__arrows")

    if (!arrows) {
      return
    }

    arrows.classList.remove(classes.hidden)
  }

  /**
   * Main carousel constructor.
   */
  function constructCarousel() {
    const carousel = document.querySelector(selectors.carousel)

    if (!carousel) {
      return
    }

    const numberOfSlides = carousel.dataset.slidesQuantity

    if (numberOfSlides <= 1) {
      return
    }

    Carousel(selectors.carousel, {
      arrows: true,
      autoHeight: true,
      height: "100%",
      type: "fade",
      rewind: true,
    }).mount()

    showArrows(carousel)
  }

  /**
   * Constructs countdown timers.
   */
  function constructCountdownTimers() {
    const countdownElements = [...document.querySelectorAll(selectors.countdown)]

    if (!countdownElements) {
      return
    }

    countdownElements.forEach((element) => {
      const targetDate = element.dataset.countdown

      initializeCountdown(targetDate, element)
    })
  }

  /**
   * Initializes a countdown timer.
   * @param {String} targetDateString - Date string in the format DD-MM-YYYY HH:MM:SS
   * @param {HTMLElement} element - HTML element to display the countdown on
   */
  function initializeCountdown(targetDateString, element) {
    if (
      !element ||
      !targetDateString ||
      element.classList.contains(classes.active)
    ) {
      return
    }

    function parseDate(input) {
      const parts = input.match(/(\d+)/g)

      return new Date(
        parts[2],
        parts[1] - 1,
        parts[0],
        parts[3],
        parts[4],
        parts[5],
      )
    }

    const targetDate = parseDate(targetDateString)

    function updateCountdown() {
      const now = new Date()
      const timeLeft = targetDate - now

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

      element.innerHTML = `${days}d : ${hours}h : ${minutes}m : ${seconds}s`

      if (timeLeft <= 0) {
        clearInterval(interval)

        element.parentElement
          .querySelector(selectors.countdownDuringCta)
          .classList.add(classes.hidden)

        const ctaAfterCountdown = element.parentElement
          .querySelector(selectors.countdownOverCta)

        if (ctaAfterCountdown) {
          ctaAfterCountdown.classList.remove(classes.hidden)
        }

        element.remove()
      }
    }

    const interval = setInterval(updateCountdown, 1000)

    element.classList.add(classes.active)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
